import { type ReactNode, type RefObject, useCallback, useEffect, useRef, useState } from "react";
import {
  FlatList,
  Platform,
  Pressable,
  type LayoutChangeEvent,
  type ListRenderItemInfo,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import "../../../theme";
import type { MediaCarouselItem, MediaCarouselThumbnailRenderProps } from "../MediaCarousel.types";

export interface MediaCarouselThumbnailStripProps<T extends MediaCarouselItem = MediaCarouselItem> {
  readonly items: readonly T[];
  readonly activeIndex: number;
  readonly onSelect: (index: number) => void;
  readonly renderThumbnail: (props: MediaCarouselThumbnailRenderProps<T>) => ReactNode;
  readonly thumbnailWidth: number;
  readonly thumbnailHeight: number;
  readonly style?: StyleProp<ViewStyle> | undefined;
  readonly testID?: string | undefined;
}

/**
 * Horizontally virtualised strip of item thumbnails.
 *
 * Backed by a `FlatList` with `getItemLayout` so only the thumbnails near the
 * viewport are mounted — a hundred-item strip fetches a handful of images, not
 * a hundred — and so the active thumbnail can be scrolled into view by index.
 */
function MediaCarouselThumbnailStripInner<T extends MediaCarouselItem>({
  items,
  activeIndex,
  onSelect,
  renderThumbnail,
  thumbnailWidth,
  thumbnailHeight,
  style,
  testID,
}: MediaCarouselThumbnailStripProps<T>) {
  const { theme } = useUnistyles();
  const listRef = useRef<FlatList<T>>(null);
  const [visibleWidth, setVisibleWidth] = useState(0);
  // Which item the strip has already been scrolled to, and whether it has moved
  // since. Together they let the effect below tell a fresh selection from a
  // re-run that has nothing new to show.
  const scrolledToIndexRef = useRef(activeIndex);
  const hasScrolledRef = useRef(false);
  const hasCentredRef = useRef(false);

  const markScrolled = useCallback(() => {
    hasScrolledRef.current = true;
  }, []);

  // Gap is applied as a trailing margin rather than `contentContainerStyle.gap`
  // so the stride below matches the real layout exactly.
  const gap = theme.spacing.sm;
  const stride = thumbnailWidth + gap;

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    setVisibleWidth((current) => (current === width ? current : width));
  }, []);

  // Gated on having measured a width at all, not on its current value: centring
  // an item needs the strip's own width, and scrolling before the first layout
  // leaves the strip at a nonsense offset that clips the leading thumbnail. Once
  // measured, later width changes (e.g. a ResizeObserver re-firing) must not
  // re-trigger this, or an unrelated resize would yank the strip back to the
  // active thumbnail and stomp on a scroll position the user set by hand.
  const hasMeasuredWidth = visibleWidth > 0;
  useEffect(() => {
    if (!hasMeasuredWidth) return;
    if (activeIndex < 0 || activeIndex >= items.length) return;
    // Selecting an item always brings its thumbnail into view. The initial
    // centring does too — but only while the strip is still at the offset it
    // mounted with. That first layout is delivered by a ResizeObserver, so on a
    // loaded machine it can land after the user has already dragged the strip,
    // and re-centring then would animate away the position they just chose.
    if (activeIndex === scrolledToIndexRef.current && hasScrolledRef.current) return;
    scrolledToIndexRef.current = activeIndex;
    // The first placement is where the strip should have opened, so it jumps
    // there; only a later change of selection is a movement worth animating.
    const animated = hasCentredRef.current;
    hasCentredRef.current = true;
    listRef.current?.scrollToIndex({ index: activeIndex, animated, viewPosition: 0.5 });
  }, [activeIndex, items.length, hasMeasuredWidth]);

  useDragToScroll(listRef, markScrolled);

  const getItemLayout = useCallback(
    (_data: ArrayLike<T> | null | undefined, index: number) => ({ length: stride, offset: stride * index, index }),
    [stride],
  );

  const renderItem = useCallback(
    ({ item, index }: ListRenderItemInfo<T>) => {
      const isActive = index === activeIndex;

      return (
        <Pressable
          onPress={() => {
            onSelect(index);
          }}
          // Tab semantics, per the ARIA carousel-with-slide-picker pattern:
          // each thumbnail selects which item the single media area shows.
          role="tab"
          aria-selected={isActive}
          accessibilityLabel={item.accessibilityLabel ?? `Item ${String(index + 1)}`}
          style={[
            styles.thumbnail,
            { width: thumbnailWidth, height: thumbnailHeight, marginRight: gap },
            isActive && styles.thumbnailActive,
            Platform.OS === "web" && styles.webInteractive,
          ]}
        >
          {renderThumbnail({ item, index, isActive })}
        </Pressable>
      );
    },
    [activeIndex, gap, onSelect, renderThumbnail, thumbnailHeight, thumbnailWidth],
  );

  return (
    <FlatList
      ref={listRef}
      data={items}
      horizontal
      keyExtractor={(item) => item.id}
      renderItem={renderItem}
      getItemLayout={getItemLayout}
      extraData={activeIndex}
      onLayout={handleLayout}
      // Covers the scrolls this component doesn't perform itself — a wheel, a
      // touch drag, the centring above: any of them means the strip is no
      // longer at the offset it mounted with.
      onScroll={markScrolled}
      // FlatList's default windowSize of 21 measures in viewport lengths, so a
      // 480pt-wide strip would keep ~10,000pt of thumbnails mounted — dozens of
      // image fetches for a strip showing five. One viewport either side is
      // enough to cover a flick without the user seeing a blank cell.
      windowSize={3}
      initialNumToRender={8}
      maxToRenderPerBatch={6}
      role="tablist"
      showsHorizontalScrollIndicator={false}
      style={[styles.strip, style]}
      testID={testID}
    />
  );
}

MediaCarouselThumbnailStripInner.displayName = "MediaCarouselThumbnailStrip";

const MediaCarouselThumbnailStrip = MediaCarouselThumbnailStripInner as <T extends MediaCarouselItem>(
  props: MediaCarouselThumbnailStripProps<T>,
) => ReturnType<typeof MediaCarouselThumbnailStripInner>;

export { MediaCarouselThumbnailStrip };

// ---------------------------------------------------------------------------
// Drag to scroll
// ---------------------------------------------------------------------------

/** Pointer travel before a press becomes a drag, in CSS pixels. */
const DRAG_THRESHOLD = 4;

/**
 * Lets a mouse drag the strip sideways on web.
 *
 * Touch already drags the list natively on every platform, so this only fills
 * the desktop gap, where the alternative is a scrollbar or a shift-wheel. The
 * drag has to coexist with tapping a thumbnail to select it: a press only
 * becomes a drag once the pointer has travelled `DRAG_THRESHOLD`, and the click
 * that the browser fires afterwards is swallowed so the strip doesn't jump to
 * whichever thumbnail happened to be under the cursor when the drag ended.
 *
 * `onScrolled` is called as the drag moves the strip. The caller needs that the
 * instant the offset changes, so it is reported here, next to the write, rather
 * than left to the scroll event the write goes on to fire.
 */
function useDragToScroll<T>(listRef: RefObject<FlatList<T> | null>, onScrolled: () => void): void {
  useEffect(() => {
    if (Platform.OS !== "web") return;

    const scrollable: unknown = listRef.current?.getScrollableNode();
    if (!(scrollable instanceof HTMLElement)) return;
    const node = scrollable;

    let activePointerId: number | null = null;
    let originX = 0;
    let originScrollLeft = 0;
    let dragging = false;
    let suppressClick = false;

    function endDrag() {
      activePointerId = null;
      dragging = false;
      node.style.removeProperty("cursor");
    }

    function handlePointerDown(event: PointerEvent) {
      // Touch and pen keep the platform's own scrolling, momentum and all.
      if (event.pointerType !== "mouse" || event.button !== 0) return;
      // Nothing to drag when the thumbnails already fit — without this, a shaky
      // click on a short strip would be swallowed as a drag.
      if (node.scrollWidth <= node.clientWidth) return;

      activePointerId = event.pointerId;
      originX = event.clientX;
      originScrollLeft = node.scrollLeft;
      dragging = false;
      suppressClick = false;
    }

    function handlePointerMove(event: PointerEvent) {
      if (activePointerId !== event.pointerId) return;

      const delta = event.clientX - originX;
      if (!dragging) {
        if (Math.abs(delta) < DRAG_THRESHOLD) return;
        dragging = true;
        node.style.setProperty("cursor", "grabbing");
      }
      node.scrollLeft = originScrollLeft - delta;
      onScrolled();
    }

    function handlePointerUp(event: PointerEvent) {
      if (activePointerId !== event.pointerId) return;
      // react-native-web dispatches Pressable's onPress from the native click
      // that follows, so the drag is undone here rather than in the press.
      suppressClick = dragging;
      endDrag();
    }

    function handlePointerCancel(event: PointerEvent) {
      if (activePointerId !== event.pointerId) return;
      endDrag();
    }

    function handleClickCapture(event: MouseEvent) {
      if (!suppressClick) return;
      suppressClick = false;
      event.stopPropagation();
      event.preventDefault();
    }

    function handleDragStart(event: DragEvent) {
      // Stops the browser's own text/image drag from pre-empting the scroll.
      event.preventDefault();
    }

    // Move and release go on the document so a drag survives the pointer
    // leaving the strip; both bail immediately unless a drag is armed.
    node.addEventListener("pointerdown", handlePointerDown);
    node.addEventListener("dragstart", handleDragStart);
    node.addEventListener("click", handleClickCapture, true);
    document.addEventListener("pointermove", handlePointerMove);
    document.addEventListener("pointerup", handlePointerUp);
    document.addEventListener("pointercancel", handlePointerCancel);

    return () => {
      node.removeEventListener("pointerdown", handlePointerDown);
      node.removeEventListener("dragstart", handleDragStart);
      node.removeEventListener("click", handleClickCapture, true);
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
      document.removeEventListener("pointercancel", handlePointerCancel);
    };
  }, [listRef, onScrolled]);
}

const styles = StyleSheet.create((theme) => ({
  strip: {
    flexGrow: 0,
    // A thumbnail is a control, not prose: without this a drag paints a text
    // selection across the strip instead of scrolling it.
    userSelect: "none",
  },
  thumbnail: {
    overflow: "hidden",
    borderRadius: theme.spacing.sm,
    borderWidth: 2,
    borderColor: "transparent",
    backgroundColor: theme.colors.borderCard,
  },
  thumbnailActive: {
    borderColor: theme.colors.interactive.controls.selected_midground,
  },
  webInteractive: Object.assign({ cursor: "pointer" } satisfies ViewStyle, {
    transition: "border-color 0.15s ease-in-out",
  }),
}));
